/**
 * API Client for BuildGravity Dialer Backend
 */

import { auth } from '../firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

/**
 * Get the authorization header with Firebase ID token
 */
async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Phone number from backend
 */
export interface PhoneNumber {
  id: string;
  phone_number: string;
  area_code: string;
  status: string;
  calls_today: number;
  calls_this_week: number;
  total_calls: number;
  last_used_at?: string;
  health_score?: number;
  answer_rate_7d?: number;
}

/**
 * Call initiation response
 */
export interface CallInitiateResponse {
  call_id: string;
  telnyx_username: string;
  telnyx_password: string;
  from_number: string;
  to_phone: string;
  status: string;
}

/**
 * List available phone numbers
 */
export async function listPhoneNumbers(): Promise<PhoneNumber[]> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/phone-numbers`, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to list phone numbers: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Initiate an outbound call
 */
export async function initiateCall(params: {
  to_phone: string;
  from_number_id: string;
  prospect_name?: string;
  prospect_id?: string;
}): Promise<CallInitiateResponse> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/calls/initiate`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to initiate call: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update call outcome
 */
export async function updateCallOutcome(params: {
  call_id: string;
  outcome: string;
  notes?: string;
  callback_date?: string;
}): Promise<{ status: string; call_id: string }> {
  const headers = await getAuthHeader();
  const { call_id, ...body } = params;

  const response = await fetch(`${API_BASE_URL}/api/calls/${call_id}/outcome`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to update call outcome: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Upload a call recording for transcription
 */
export async function uploadCallRecording(params: {
  call_id: string;
  audioBlob: Blob;
}): Promise<{ call_id: string; status: string; transcript?: string }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const formData = new FormData();
  formData.append('file', params.audioBlob, 'recording.webm');

  const response = await fetch(`${API_BASE_URL}/api/calls/${params.call_id}/recording`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to upload recording: ${response.statusText}`);
  }

  return response.json();
}
